import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Clock, Copy, Wrench, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import 'highlight.js/styles/github-dark.css';
import 'katex/dist/katex.min.css';
import { InlineChart } from './InlineChart';
import { highlightMenuPaths } from './highlight-menu-paths';
import { clipboardPayloadFromRendered } from './clipboard-from-rendered';
import { hljs, parseAiMarkdown } from '../markdown/ai-marked';
import { ToolsDetailDialog, type ToolDetailItem } from '../ToolsDetailDialog';
import { useToast } from '@/hooks/use-toast';
import type { ChatMessage as ChatMessageType } from '../types';

/** Splits content into markdown and chart segments. Chart blocks are not shown as raw code. */
function splitContentSegments(
  content: string
): Array<{ type: 'markdown'; content: string } | { type: 'chart'; code: string }> {
  const segments: Array<{ type: 'markdown'; content: string } | { type: 'chart'; code: string }> =
    [];
  const chartBlockRe = /```chart\s*\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = chartBlockRe.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'markdown', content: content.slice(lastIndex, match.index) });
    }
    segments.push({ type: 'chart', code: match[1].trim() });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) {
    segments.push({ type: 'markdown', content: content.slice(lastIndex) });
  }
  if (segments.length === 0) {
    segments.push({ type: 'markdown', content });
  }
  return segments;
}

interface ChatMessageProps {
  message: ChatMessageType;
  isStreaming?: boolean;
  /** Tool executions for footer (e.g. streaming); persisted messages use message.functionResults for count */
  toolCalls?: { name: string; executionTimeMs: number }[];
}

function formatResponseTime(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function formatTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

/** Copy rendered body as rich HTML + plain text (KaTeX restored to LaTeX source). */
async function copyRenderedContent(el: HTMLElement): Promise<void> {
  const { plain, html } = clipboardPayloadFromRendered(el);
  if (!plain) throw new Error('Nothing to copy');

  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([plain], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        }),
      ]);
      return;
    } catch {
      // Fall through to plain-text copy (some browsers reject text/html).
    }
  }

  await navigator.clipboard.writeText(plain);
}

/** Renders markdown HTML (for a single segment). */
function MarkdownSegment({ content, isUser }: { content: string; isUser: boolean }) {
  useEffect(() => {
    document.querySelectorAll('pre code:not(.hljs)').forEach((block) => {
      hljs.highlightElement(block as HTMLElement);
    });
  }, [content]);

  if (!content.trim()) return null;
  const html = parseAiMarkdown(highlightMenuPaths(content));
  return (
    <div
      className={cn('chat-markdown', isUser && 'chat-markdown--user')}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/** Renders assistant message content: markdown segments + inline charts from ```chart code blocks. */
function AssistantContent({ content, isUser }: { content: string; isUser: boolean }) {
  const segments = useMemo(() => splitContentSegments(content), [content]);
  return (
    <>
      {segments.map((seg, i) =>
        seg.type === 'markdown' ? (
          <MarkdownSegment key={i} content={seg.content} isUser={isUser} />
        ) : (
          <InlineChart key={i} code={seg.code} />
        )
      )}
    </>
  );
}

/** Renders user/system message content as plain markdown (no chart parsing). */
function MessageContent({ content, isUser }: { content: string; isUser: boolean }) {
  useEffect(() => {
    document.querySelectorAll('pre code:not(.hljs)').forEach((block) => {
      hljs.highlightElement(block as HTMLElement);
    });
  }, [content]);

  if (!content.trim()) return null;
  const html = parseAiMarkdown(highlightMenuPaths(content));
  return (
    <div
      className={cn('chat-markdown', isUser && 'chat-markdown--user')}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export function ChatMessage({
  message,
  isStreaming = false,
  toolCalls: toolCallsOverride,
}: ChatMessageProps) {
  const { toast } = useToast();
  const contentRef = useRef<HTMLDivElement>(null);
  const [toolsDialogOpen, setToolsDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const isAssistant = message.role === 'assistant';
  const toolCount =
    toolCallsOverride?.length ?? message.toolCalls?.length ?? message.functionResults?.length ?? 0;

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async () => {
    const el = contentRef.current;
    if (!el) return;
    try {
      await copyRenderedContent(el);
      setCopied(true);
      toast({
        title: 'Copied',
        description: 'Response copied to clipboard',
      });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to copy response',
      });
    }
  };

  const toolDetailItems: ToolDetailItem[] = useMemo(() => {
    if (message.functionResults?.length) {
      return message.functionResults.map((fr) => ({
        name: fr.functionName,
        executionTimeMs: fr.executionTime ?? 0,
        functionCalls: fr.functionCalls,
        resultData: fr.resultData,
      }));
    }
    if (toolCallsOverride?.length) {
      return toolCallsOverride.map((tc) => ({
        name: tc.name,
        executionTimeMs: tc.executionTimeMs,
      }));
    }
    return [];
  }, [message.functionResults, toolCallsOverride]);

  const dateTimeLabel = message.timestamp.toLocaleString([], {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  const timeOnly = message.timestamp.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="flex w-full flex-col items-stretch">
      <div className={cn('flex w-full', isUser ? 'justify-end' : 'justify-start')}>
        <div
          className={cn(
            'max-w-[85%] rounded-2xl px-4 py-2 text-sm',
            isAssistant && 'min-w-[50%]',
            isUser && 'bg-primary text-primary-foreground',
            !isUser && !isSystem && 'bg-muted text-foreground',
            isSystem && 'bg-muted/50 text-muted-foreground italic'
          )}
        >
          <div ref={contentRef}>
            {message.content ? (
              isAssistant ? (
                <AssistantContent content={message.content} isUser={false} />
              ) : (
                <MessageContent content={message.content} isUser={isUser} />
              )
            ) : isStreaming ? (
              <div className="flex items-center gap-1">
                <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.3s]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.15s]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" />
              </div>
            ) : null}
            {isStreaming && message.content && (
              <span className="inline-block h-4 w-0.5 animate-pulse bg-foreground" />
            )}
          </div>
          {message.errorMessage && (
            <div className="mt-1 text-[10px] text-destructive">{message.errorMessage}</div>
          )}
          {isAssistant ? (
            <div className="mt-2 border-t border-border/60 pt-2">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[10px] text-muted-foreground">
                {isStreaming && toolCount > 0 ? (
                  <button
                    type="button"
                    onClick={() => setToolsDialogOpen(true)}
                    className="flex items-center gap-1 rounded hover:bg-muted/80 hover:text-foreground transition-colors cursor-pointer"
                    title="View tool details"
                  >
                    <Wrench className="h-3 w-3" />
                    Using tools ({toolCount})
                  </button>
                ) : toolCount > 0 ? (
                  <button
                    type="button"
                    onClick={() => setToolsDialogOpen(true)}
                    className="flex items-center gap-1 rounded hover:bg-muted/80 hover:text-foreground transition-colors cursor-pointer"
                    title="View tool details"
                  >
                    <Wrench className="h-3 w-3" />
                    {toolCount} {toolCount === 1 ? 'tool' : 'tools'}
                  </button>
                ) : null}
                {toolCount > 0 && (
                  <ToolsDetailDialog
                    open={toolsDialogOpen}
                    onOpenChange={setToolsDialogOpen}
                    items={toolDetailItems}
                  />
                )}
                {message.tokens != null && message.tokens > 0 && (
                  <span className="flex items-center gap-1" title="Tokens used">
                    <Zap className="h-3 w-3" />
                    {formatTokens(message.tokens)} tokens
                  </span>
                )}
                {message.streamedCost != null && message.streamedCost > 0 && (
                  <span className="flex items-center gap-1" title="Estimated cost (USD)">
                    $
                    {message.streamedCost >= 0.01
                      ? message.streamedCost.toFixed(2)
                      : message.streamedCost.toFixed(4)}
                  </span>
                )}
                {!isStreaming && message.responseTime != null && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatResponseTime(message.responseTime)}
                  </span>
                )}
                <span title={dateTimeLabel}>{dateTimeLabel}</span>
                {!isStreaming && message.content.trim() ? (
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="ml-auto flex items-center gap-1 rounded p-0.5 hover:bg-muted/80 hover:text-foreground transition-colors cursor-pointer"
                    title={copied ? 'Copied' : 'Copy response'}
                    aria-label={copied ? 'Copied' : 'Copy response'}
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                ) : null}
              </div>
            </div>
          ) : isUser ? (
            <div className="mt-2 border-t border-border/60 pt-2">
              <div className="text-[10px] text-primary-foreground/80">
                <span title={dateTimeLabel}>{dateTimeLabel}</span>
              </div>
            </div>
          ) : !isStreaming ? (
            <span className="mt-1 block text-[10px] text-muted-foreground">{timeOnly}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
